import store from '@state/store'
import api from '@utils/api'

const ALLOWED_USER_ROLES = [
  'SUPERUSER',
  'DICTIONARIES_OPERATOR',
  'DOCUMENTS_OPERATOR',
  'GUEST',
]

export default [
  {
    path: '/',
    name: 'home',
    component: () => lazyLoadView(import('@views/home')),
    meta: {
      authRequired: true,
      permission: [],
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => lazyLoadView(import('@pages/dashboard/dashboard')),
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'REPORTS_OPERATOR'],
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@pages/login/login')),
    beforeEnter(routeTo, routeFrom, next) {
      if (store.getters['auth/loggedIn']) {
        next({ name: 'home' })
      } else if (store.getters['isReady']) {
        next({ name: 'home' })
      } else {
        next()
      }
    },
  },
  {
    path: '/test',
    name: 'test',
    component: () => lazyLoadView(import('@views/test')),
  },
  {
    path: '/documents/searchPlug',
    name: 'searchPlug',
    component: () => lazyLoadView(import('@pages/search-plug')),
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
  },
  {
    path: '/documents/:documentType',
    name: 'documents',
    component: () => lazyLoadView(import('@pages/documents/documents')),
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
  },
  {
    path: '/documents/:documentType/new',
    name: 'newDocument',
    component: () => lazyLoadView(import('@pages/documents/document-new')),
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
  },
  {
    path: '/documents/:documentType/:documentId/',
    name: 'document',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () => lazyLoadView(import('@pages/documents/document')),
    beforeEnter(routeTo, routeFrom, next) {
      api.getDocument(routeTo.params.documentId).then(response => {
        routeTo.params.document = response
        next()
      })
    },
    props: route => ({ document: route.params.document }),
  },
  {
    path: '/initialization',
    name: 'initialization',
    redirect: '404',
  },
  {
    path: '/systemSettings',
    name: 'systemSettingsList',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER'],
    },
    component: () =>
      lazyLoadView(import('@pages/system-settings/system-settings-list')),
  },
  {
    path: '/systemSettings/:dictionaryName',
    name: 'systemSettings',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER'],
    },
    component: () =>
      lazyLoadView(import('@pages/system-settings/system-settings')),
    beforeEnter(routeTo, routeFrom, next) {
      api.getDictionary(routeTo.params.dictionaryName).then(response => {
        routeTo.params.dictionary = response
        next()
      })
    },
    props: route => ({ dictionary: route.params.dictionary }),
  },
  {
    path: '/control/settings',
    name: 'settings',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER'],
    },
    component: () => lazyLoadView(import('@pages/control/settings')),
  },
  {
    path: '/control/settings/:code',
    name: 'setting',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER'],
    },
    component: () => lazyLoadView(import('@pages/control/setting')),
    beforeEnter(routeTo, routeFrom, next) {
      api.getGlobalSettingByCode(routeTo.params.code).then(response => {
        routeTo.params.setting = response
        next()
      })
    },
    props: route => ({ setting: route.params.setting }),
  },
  {
    path: '/control/users',
    name: 'users',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER'],
    },
    component: () => lazyLoadView(import('@pages/control/users')),
  },
  {
    path: '/control/users/:userId',
    name: 'user',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER'],
    },
    component: () => lazyLoadView(import('@pages/control/user')),
    beforeEnter(routeTo, routeFrom, next) {
      let userRoles = api.getUserRoles(routeTo.params.userId)
      let userRegions = api.getUserRegions(routeTo.params.userId)
      let userOrganisations = api.getUserOrganisations(routeTo.params.userId)
      api.getUserById(routeTo.params.userId).then(response => {
        routeTo.params.user = response
        api
          .getPermissions([userRoles, userRegions, userOrganisations])
          .then(response => {
            routeTo.params.roles = response.roles
            routeTo.params.regions = response.regions
            routeTo.params.organisations = response.organisations
            next()
          })
      })
    },
    props: route => ({
      user: route.params.user,
      roles: route.params.roles,
      regions: route.params.regions,
      organisations: route.params.organisations,
    }),
  },
  {
    path: '/control/roles',
    name: 'roles',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER'],
    },
    component: () => lazyLoadView(import('@pages/control/roles')),
  },
  {
    path: '/dictionaries/contacts',
    name: 'contacts',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () =>
      lazyLoadView(import('@pages/dictionaries/contacts/contacts')),
  },
  {
    path: '/dictionaries/contacts/:contactId',
    name: 'contact',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () =>
      lazyLoadView(import('@pages/dictionaries/contacts/contact')),
    beforeEnter(routeTo, routeFrom, next) {
      api.getContactById(routeTo.params.contactId).then(response => {
        routeTo.params.contact = response
        next()
      })
    },
    props: route => ({ contact: route.params.contact }),
  },
  {
    path: '/dictionaries/goods',
    name: 'goods',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () => lazyLoadView(import('@pages/dictionaries/goods/goods')),
  },
  {
    path: '/dictionaries/goods/:goodsId',
    name: 'goodsItem',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () =>
      lazyLoadView(import('@pages/dictionaries/goods/goods-item')),
    beforeEnter(routeTo, routeFrom, next) {
      api.getGoodsById(routeTo.params.goodsId).then(response => {
        routeTo.params.goodsItem = response
        const group = response.group
        api
          .getGoodsCharacteristic({
            goodsId: response.id,
            params: { size: 10, page: 1 },
          })
          .then(async response => {
            routeTo.params.characteristic = response
            if (group === 4) {
              await api
                .getGoodsDrumvolume(routeTo.params.goodsId)
                .then(response => {
                  routeTo.params.drumvolume = response
                })
            }
            if (group === 5) {
              await api
                .getGoodsEquippedbarrel(routeTo.params.goodsId)
                .then(response => {
                  routeTo.params.equippedbarrel = response
                })
            }
            if (group === 6) {
              await api
                .getGoodsDrumoil(routeTo.params.goodsId)
                .then(response => {
                  routeTo.params.drumoil = response
                })
            }
            next()
          })
      })
    },
    props: route => ({
      goods: route.params.goodsItem,
      characteristic: route.params.characteristic,
      drumoil: route.params.drumoil,
      drumvolume: route.params.drumvolume,
      equippedbarrel: route.params.equippedbarrel,
    }),
  },
  {
    path: '/dictionaries/organizations',
    name: 'organizations',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () =>
      lazyLoadView(import('@pages/dictionaries/organization/organizations')),
  },
  {
    path: '/dictionaries/organizations/:orgId',
    name: 'organization',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () =>
      lazyLoadView(
        import('@pages/dictionaries/organization/organization-item')
      ),
    beforeEnter(routeTo, routeFrom, next) {
      api.getItemById('organization', routeTo.params.orgId).then(response => {
        routeTo.params.organization = response
        api
          .getSubItemById('organization', routeTo.params.orgId, 'communication')
          .then(response => {
            routeTo.params.communication = response
            api
              .getSubItemById('organization', routeTo.params.orgId, 'address')
              .then(response => {
                routeTo.params.address = response
                next()
              })
          })
      })
    },
    props: route => ({
      organization: route.params.organization,
      communication: route.params.communication,
      address: route.params.address,
    }),
  },
  {
    path: '/dictionaries/list',
    name: 'dictionariesList',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () =>
      lazyLoadView(
        import('@pages/dictionaries/dictionaries/dictionaries-list')
      ),
  },
  {
    path: '/dictionaries/:dictionary',
    name: 'dictionary',
    meta: {
      authRequired: true,
      permission: ALLOWED_USER_ROLES,
    },
    component: () =>
      lazyLoadView(import('@pages/dictionaries/dictionaries/data-table')),
  },
  {
    path: '/events',
    name: 'events',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'EVENTS_OPERATOR'],
    },
    component: () => lazyLoadView(import('@views/events/events')),
  },
  {
    path: '/remains',
    name: 'remains',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'EVENTS_OPERATOR'],
    },
    component: () => lazyLoadView(import('@views/remains')),
  },
  {
    path: '/heartbeats',
    name: 'heartbeats',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'EVENTS_OPERATOR'],
    },
    component: () => lazyLoadView(import('@views/heartbeats/heartbeats')),
  },
  {
    path: '/heartbeats/:id',
    name: 'heartbeats',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'EVENTS_OPERATOR'],
    },
    component: () => lazyLoadView(import('@views/heartbeats/heartbeats')),
  },
  {
    path: '/heartbeats/connectedPlugs/:id',
    name: 'heartbeats',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'EVENTS_OPERATOR'],
    },
    component: () => lazyLoadView(import('@views/connected-plugs')),
  },
  {
    path: '/warehouses',
    name: 'warehouses',
    meta: {
      authRequired: true,
    },
    component: () => lazyLoadView(import('@pages/warehouses')),
  },
  {
    path: '/warehouses/:id',
    name: 'warehouse',
    meta: {
      authRequired: true,
    },
    component: () => lazyLoadView(import('@pages/warehouses/id')),
  },
  {
    path: '/workshops',
    name: 'workshops',
    meta: {
      authRequired: true,
    },
    component: () => lazyLoadView(import('@pages/workshops')),
  },
  {
    path: '/workshops/:id',
    name: 'workshop',
    meta: {
      authRequired: true,
    },
    component: () => lazyLoadView(import('@pages/workshops/id')),
  },
  {
    path: '/devices',
    name: 'devices',
    meta: {
      authRequired: true,
    },
    component: () => lazyLoadView(import('@pages/devices/devices')),
  },
  {
    path: '/accountSettings',
    name: 'account',
    component: () => lazyLoadView(import('@pages/account')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/changePassword',
    name: 'password',
    component: () => lazyLoadView(import('@pages/password')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/changePassword/user/:user_name/token/:token',
    name: 'password',
    component: () => lazyLoadView(import('@pages/reset-password')),
    meta: {
      authRequired: false,
    },
  },
  {
    path: '/forgotPassword',
    name: 'forgotPassword',
    component: () => lazyLoadView(import('@pages/forgot-password')),
    meta: {
      authRequired: false,
    },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => lazyLoadView(import('@pages/reports/reports')),
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'REPORTS_OPERATOR'],
    },
  },
  {
    path: '/reports/:name',
    name: 'reports-view',
    component: () => lazyLoadView(import('@pages/reports/views')),
    props: {
      header: true,
      content: true,
    },
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'REPORTS_OPERATOR'],
    },
  },
  {
    path: '/alerts',
    name: 'alerts',
    component: () => lazyLoadView(import('@pages/reports/alerts')),
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'REPORTS_OPERATOR'],
    },
  },
  {
    path: '/alerts/InvalidMeasurement/:id',
    name: 'invalidEvents',
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'EVENTS_OPERATOR'],
    },
    component: () =>
      lazyLoadView(
        import('@pages/reports/views/InvalidMeasurement/invalid-events')
      ),
  },
  {
    path: '/alerts/:name',
    name: 'alerts-view',
    component: () => lazyLoadView(import('@pages/reports/views')),
    meta: {
      authRequired: true,
      permission: ['SUPERUSER', 'REPORTS_OPERATOR'],
    },
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/404').default,
    props: true,
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/403',
    name: '403',
    component: require('@views/403').default,
    props: true,
    meta: {
      authRequired: true,
    },
  },
  {
    path: '*',
    redirect: '404',
  },
]

function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    loading: require('@views/loading').default,
    error: require('@views/timeout').default,
    delay: 400,
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      return h(AsyncHandler, data, children)
    },
  })
}
